import {forwardRef, useEffect, useRef, useState} from 'react';
import {
  DialogContent,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
  Root as DialogRoot,
} from '@radix-ui/react-dialog';
import {
  DropdownMenuContent,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
  Root as DropdownMenuRoot,
} from '@radix-ui/react-dropdown-menu';
import cx from 'classnames';

import {Icon} from '#component/base/Icon';
import {Radio} from '#component/base/Radio';
import {Text} from '#component/base/Text';
import {TextInput} from '#component/base/TextInput';
import {useViewportSize} from '#hook';
import {sprinkles} from '#style/sprinkles';

import {SelectProps} from './Select.types';

import * as css from './Select.css';

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {className, id, options = [], placeholder, value, variant = 'primary', label, ...otherProps},
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const selectRef = useRef<HTMLSelectElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const triggerRect = triggerRef.current?.getBoundingClientRect();
    const {width} = triggerRect || {};
    const {isMobile} = useViewportSize();
    const [uncontrolledValue, setUncontrolledValue] = useState(selectRef.current?.value || '');
    const currentValue = value || uncontrolledValue;
    const isSecondary = variant === 'secondary';
    const Root = isMobile ? DialogRoot : DropdownMenuRoot;
    const Trigger = isMobile ? DialogTrigger : DropdownMenuTrigger;

    const handleChange = (nextValue: string) => {
      if (selectRef.current) {
        // With these next two lines, we're setting the value of the field's
        // rendered <select> HTML element, and then manually triggering a
        // 'change' event from that element.
        //
        // This allows changes to the <selects>'s value made via the non-native
        // dropdown menu to trigger the elements's `onChange` handler with the
        // same event signature and target element, rather than calling it
        // manually, or using a custom handler with a different call signature.
        selectRef.current.value = nextValue;
        selectRef.current.dispatchEvent(new Event('change', {bubbles: true}));
        setUncontrolledValue(nextValue);
      }
    };

    useEffect(() => {
      // If a `ref` value has been forwarded, we need to share current
      // `formRef` value with it on every render.
      if (selectRef && ref) {
        if (typeof ref === 'function') {
          ref(selectRef.current);
        } else {
          // eslint-disable-next-line no-param-reassign
          ref.current = selectRef.current;
        }
      }
    });

    useEffect(() => {
      if (selectRef.current?.value !== uncontrolledValue) {
        setUncontrolledValue(selectRef.current?.value || '');
      }
    }, [setUncontrolledValue, uncontrolledValue]);
    return (
      <div className={cx(css.root, className)}>
        <Root open={isOpen} onOpenChange={setIsOpen}>
          <Trigger
            aria-labelledby={id}
            className={sprinkles({flex: 'flex', w: 'full'})}
            ref={triggerRef}>
            <TextInput
              as="select"
              className={cx({[css.secondaryStyles]: isSecondary})}
              color={isSecondary ? 'orange' : undefined}
              id={id}
              inputClassName={css.selectStyles}
              readOnly
              ref={selectRef}
              rightContent={
                <Icon
                  className={css.caretStyles}
                  color="silver"
                  name="CaretDown"
                  style={{transform: isOpen ? 'rotate(-180deg)' : 'none'}}
                />
              }
              tabIndex={-1}
              type="select"
              value={currentValue}
              {...otherProps}>
              {placeholder && (
                <option disabled hidden value="">
                  {placeholder}
                </option>
              )}
              {options.map(({label, value: optionValue}, i) => (
                <option key={i} value={optionValue}>
                  {label}
                </option>
              ))}
            </TextInput>
          </Trigger>

          {isMobile ? (
            <DialogPortal>
              <DialogOverlay />
              <DialogContent className={css.mobileContent}>
                <Text
                  as={DialogTitle}
                  className={css.label}
                  fontWeight="semiBold"
                  variant="heading4">
                  <span>{label || 'Select an option'}</span>

                  <Icon aria-hidden color="doveGray" name="CaretDown" weight="bold" />
                </Text>

                <ul className={css.mobileList}>
                  {options.map(({label, value: optionValue}, i) => {
                    const isChecked = optionValue === currentValue;

                    return (
                      <li key={i}>
                        <Text
                          aria-checked={isChecked}
                          as="button"
                          className={sprinkles({flex: 'flex', gap: 16, p: 16, w: 'full'})}
                          color={isChecked ? 'black' : 'doveGray'}
                          onClick={() => {
                            handleChange(optionValue);
                            setIsOpen(false);
                          }}>
                          {variant !== 'secondary' && (
                            <Radio aria-hidden checked={isChecked} readOnly tabIndex={-1} />
                          )}
                          {label}
                        </Text>
                      </li>
                    );
                  })}
                </ul>
              </DialogContent>
            </DialogPortal>
          ) : (
            <DropdownMenuPortal>
              <DropdownMenuContent
                align="start"
                className={css.content}
                sideOffset={4}
                style={{width}}>
                <DropdownMenuRadioGroup onValueChange={handleChange} value={currentValue}>
                  {options.map(({label, value: optionValue}, i) => {
                    const isChecked = optionValue === currentValue;

                    return (
                      <DropdownMenuRadioItem key={i} textValue={label} value={optionValue}>
                        <Text
                          as="label"
                          className={sprinkles({flex: 'flex', gap: 16, p: 16})}
                          color={isChecked ? 'black' : 'doveGray'}>
                          {variant !== 'secondary' && <Radio checked={isChecked} readOnly />}
                          {label}
                        </Text>
                      </DropdownMenuRadioItem>
                    );
                  })}
                </DropdownMenuRadioGroup>
              </DropdownMenuContent>
            </DropdownMenuPortal>
          )}
        </Root>
      </div>
    );
  },
);
